import { Component, inject } from '@angular/core';
import { CONFIG_CONTEXT } from 'src/app/enum/config-context.enum';
import { StatisticTypeEnum } from 'src/app/enum/statistic-type.enum';
import { CarpentryService } from 'src/app/services/configurators-2d/applications/carpentry.service';
import { FeatureSettingsService } from 'src/app/services/feature-settings/feature-settings.service';
import {
  FidelityCardTypeEnum,
  FidelityCardPointSimulationDto,
  GetCustomerFidelityInfoInput,
} from 'src/app/services/fidelity/fidelity';
import { FidelityService } from 'src/app/services/fidelity/fidelity.service';
import { PaytweakRequestInput } from 'src/app/services/fidelity/paytweak';
import { logError } from 'src/app/services/helpers/sentry.helper';
import { ModalService, MODAL } from 'src/app/services/modal.service';
import { StatisticService } from 'src/app/services/statistic.service';
import { CarpentryConfigurationData } from 'src/app/stores/tunnel/model/configuration-data/carpentry-configuration-data';
import {
  PricingState,
  InstallationState,
  FundingState,
  CustomerState,
  Funding,
} from 'src/app/stores/tunnel/model/tunnel-data-state';
import { TunnelDataStore } from 'src/app/stores/tunnel/tunnel-data.store';
import { TunnelNavigationStore } from 'src/app/stores/tunnel/tunnel-navigation.store';
import { BaseTunnelObject } from '../../model/base-tunnel-object';

@Component({
  selector: 'ng-casto-carpentry-tunnel-validation',
  templateUrl: './carpentry-tunnel-validation.component.html',
  styleUrl: './carpentry-tunnel-validation.component.scss',
})
export class CarpentryTunnelValidationComponent extends BaseTunnelObject {
  // Services
  protected carpentryService = inject(CarpentryService);
  private readonly modalService = inject(ModalService);
  private readonly statisticService = inject(StatisticService);
  private readonly fidelityService = inject(FidelityService);
  protected readonly featureSettingsService = inject(FeatureSettingsService);

  // Properties
  protected currentPricing: PricingState;
  protected currentInstallation: InstallationState;
  protected currentWithInstallation: boolean;
  protected currentFunding: FundingState;
  protected currentCustomer: CustomerState;
  protected loading: boolean = false;
  protected fidelityLoading: boolean = false;
  private carpentryConfiguration: CarpentryConfigurationData;
  protected refreshLoading: boolean = false;
  protected readonly FidelityCardTypeEnum = FidelityCardTypeEnum;

  constructor() {
    const tunnelNavigationStore = inject(TunnelNavigationStore);
    const tunnelDataStore = inject(TunnelDataStore);
    super(tunnelNavigationStore, tunnelDataStore);

    this.tunnelNavigationStore = tunnelNavigationStore;
    this.tunnelDataStore = tunnelDataStore;
  }

  get disableInstallation(): boolean {
    return this.carpentryService.isInstallationDisabled();
  }

  get currentFidelity(): FidelityCardPointSimulationDto {
    if (this.currentPricing?.fidelity) {
      return this.currentPricing.fidelity[this.currentWithInstallation ? 0 : 1];
    }
    return undefined;
  }

  get isCardExpired() {
    if (!this.currentCustomer.fidelity?.expiredDate) return false;
    let today = new Date();
    let dateToNotice = new Date();

    const nbDaysNoticeEndSubscriptionPaidCard = 30;
    //Ajout de la date de notification en fonction du setting
    dateToNotice.setTime(
      this.currentCustomer.fidelity.expiredDate.getTime() - nbDaysNoticeEndSubscriptionPaidCard * 24 * 60 * 60 * 1000
    );
    return today > dateToNotice;
  }

  get premiumCardEarnedCoupons(): number {
    let count = 0;
    this.currentFidelity?.premiumCardCoupons?.forEach(coupon => {
      count += coupon.count;
    });
    return count;
  }

  get funding(): Funding {
    return this.currentFunding[
      this.currentInstallation.withInstallation ? 'fundingWithInstallation' : 'fundingWithoutInstallation'
    ];
  }

  ngOnInit(): void {
    super.ngOnInit();
    //Envoi de la stat d'accès à la page de simulation de validation
    this.statisticService.sendStatistic(
      StatisticTypeEnum.VALIDATION_PER_PROJECT_COUNT,
      1,
      CONFIG_CONTEXT.CARPENTRY,
      this.carpentryConfiguration?.configurationId
    );
    this.computeFidelityPoint().then();
  }

  private async computeFidelityPoint() {
    await this.carpentryService.computeFidelityPoint({
      amount: this.currentPricing.totalWithoutFunding.actualAmount,
      customerId: this.currentCustomer.userData.kfId,
    });
  }

  protected subscribeToObservable() {
    this.subscriptionList.push(
      this.tunnelDataStore.getCurrentPricing().subscribe(currentPricing => {
        if (currentPricing) {
          this.currentPricing = currentPricing;
        }
      }),
      this.tunnelDataStore.getCurrentInstallation().subscribe(async currentInstallation => {
        if (currentInstallation) {
          this.currentInstallation = currentInstallation;
        }
      }),
      this.tunnelDataStore.getCurrentWithInstallation().subscribe(async currentWithInstallation => {
        if (currentWithInstallation !== undefined && this.currentWithInstallation !== currentWithInstallation) {
          let lastCurrentValue = this.currentWithInstallation;
          this.currentWithInstallation = currentWithInstallation;
          //Nathan - 08/07 : Ces lignes étaient commentées par Benjamin, surement pour essayer d'opti les appels
          if (lastCurrentValue !== null) {
            await this.updateFunding();
          }
        }
      }),
      this.tunnelDataStore.getCurrentFunding().subscribe(async currentFunding => {
        if (currentFunding) {
          this.currentFunding = currentFunding;
        }
      }),
      this.tunnelDataStore.getFidelityLoading().subscribe(async fidelityLoading => {
        this.fidelityLoading = fidelityLoading;
      }),
      this.tunnelDataStore.getCurrentCustomer().subscribe(async customer => {
        if (customer) {
          this.currentCustomer = customer;
        }
      }),
      this.tunnelDataStore.getConfigurationData().subscribe(configurationData => {
        if (configurationData) {
          this.carpentryConfiguration = configurationData.get(CONFIG_CONTEXT.CARPENTRY) as CarpentryConfigurationData;
        }
      })
    );
  }

  protected async setWithInstallation(withInstallation: boolean) {
    await this.carpentryService.updateWithInstallation(withInstallation);
    this.tunnelDataStore.setWithInstallation(withInstallation);
  }

  openDetail() {
    this.modalService.display(MODAL.INSTALLATION_DETAIL, {
      installationDetails: this.currentInstallation.detailInstallation,
      bom: this.carpentryConfiguration?.bom,
    });
  }

  protected async updateFunding() {
    try {
      this.loading = true;
      await this.computeFunding();
    } catch (error) {
      this.loading = false;
    } finally {
      this.loading = false;
    }
  }

  private async computeFunding() {
    await this.carpentryService.computeFunding({
      tunnelConfigurationId: this.carpentryConfiguration.configurationId,
      downPaymentAmount: this.currentFunding.downPayment,
      shouldRecalculateFinancing: true,
    });
  }

  private async createFidelityCard(input: { withPremium: boolean }): Promise<void> {
    if (!(await this.featureSettingsService.isFidelityEnabled())) {
      return;
    }

    this.modalService.display(MODAL.CUSTOMER_INFO_FORM, {
      defaultData: {
        customer: this.currentCustomer.userData,
        address: this.currentCustomer.userData.address,
        marketingConsent: this.currentCustomer.userData.marketingConsent,
      },
      upgrade: input.withPremium,
      statisticType: input.withPremium
        ? StatisticTypeEnum.NEW_PREMIUM_CARD_VALIDATION
        : StatisticTypeEnum.NEW_FREE_CARD_VALIDATION,
    });
  }

  protected async createPremiumCard() {
    await this.createFidelityCard({
      withPremium: true,
    });
  }

  protected async createFreeCard() {
    await this.createFidelityCard({
      withPremium: false,
    });
  }

  private buildPaytweakRequestInput(): PaytweakRequestInput {
    return {
      customerId: this.currentCustomer.userData.kfId,
      cardNumber: this.currentFidelity.cardNumber,
      firstname: this.currentCustomer.userData.firstname,
      lastname: this.currentCustomer.userData.lastname,
      address: this.currentCustomer.userData.address.line1,
      postalCode: this.currentCustomer.userData.address.postalCode,
      city: this.currentCustomer.userData.address.city,
      country: this.currentCustomer.userData.address.country,
      phone: this.currentCustomer.userData.phoneNumber,
      email: this.currentCustomer.userData.email,
    };
  }

  private async showPaytweakQrCode() {
    if (!(await this.featureSettingsService.isFidelityEnabled())) {
      return;
    }

    let input: PaytweakRequestInput = this.buildPaytweakRequestInput();

    this.modalService.display(MODAL.FIDELITY_PAYMENT_QRCODE, {
      paytweakRequestInput: input,
      onCloseCallback: undefined,
    });
  }

  private async postRenewPremiumCard() {
    if (!(await this.featureSettingsService.isFidelityEnabled())) {
      return;
    }

    const input: GetCustomerFidelityInfoInput = {
      idKingfisher: this.currentCustomer.userData.kfId,
    };
    if (this.currentCustomer.userData.kfId != null) {
      try {
        await this.fidelityService.postRenewPremiumCard(input);
      } catch (error) {
        logError(error);
      } finally {
        await this.showPaytweakQrCode();
      }
    }
  }

  protected async updatePremiumCard() {
    await this.postRenewPremiumCard();
  }

  protected async getPaymentLink() {
    await this.showPaytweakQrCode();
  }

  protected async refreshFidelity() {
    try {
      this.refreshLoading = true;
      await this.tunnelDataStore.loadCurrentCustomer(this.currentCustomer.userData);
    } catch (error) {
      logError(error);
    } finally {
      this.refreshLoading = false;
    }
  }

  protected fidelityEnabled(): boolean {
    return this.featureSettingsService.fidelityEnabled;
  }

}
